import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ENTITLEMENT_SOURCE_LABELS, formatBytes, type DownloadTicket, type EntitlementSource, type LibraryItem } from '@va/shared';
import { api, errorMessage } from '../api/client';
import { useLibrary } from '../api/queries';
import { IconCart, IconCheck, IconClose, IconDownload, IconFolder, IconMonitor, IconRefresh, IconTrash } from '../components/icons';
import { daysUntil, Empty, ErrorBox, formatDate, Loading } from '../components/ui';
import { desktop, isDesktop, mediaUrl, needsUpdate, type LicenseStatus } from '../platform';
import { useCart } from '../state/cart';
import { installedMessage, pickBuild, useInstalls } from '../state/installs';
import { useLicenseStatus } from '../state/license';
import { useToast } from '../state/toast';

const SOURCE_HINTS: Record<EntitlementSource, string> = {
  purchase: 'You bought it',
  bundle: 'Part of a bundle you bought',
  team: 'Included in your team license',
  gift: 'A gift',
  admin: 'Added to your account by Visual Artform',
  trial: 'Free trial',
};

/**
 * How the account has the product (bought, granted, team, trial...), shown beside its
 * activation. Null for a plain demo: its Demo badge says it all.
 */
function ownershipOf(item: LibraryItem): { label: string; hint: string; tone: 'plain' | 'warning' | 'muted' } | null {
  const o = item.product.ownership;
  if (!o?.owned) {
    if (item.entitlements.some((e) => e.source === 'trial')) return { label: 'Trial ended', hint: 'The free trial is over', tone: 'muted' };
    return item.demo ? null : { label: 'Expired', hint: 'This license has ended', tone: 'muted' };
  }
  if (o.sources.every((s) => s === 'trial')) {
    const d = daysUntil(o.expiresAt);
    return { label: `Trial · ${Math.max(0, d ?? 0)} days left`, hint: SOURCE_HINTS.trial, tone: 'warning' };
  }
  const label = o.sources.map((s) => ENTITLEMENT_SOURCE_LABELS[s]).join(' · ');
  return {
    label: o.expiresAt ? `${label} · until ${formatDate(o.expiresAt)}` : label,
    hint: o.sources.map((s) => SOURCE_HINTS[s]).join(' · '),
    tone: 'plain',
  };
}

interface Activation {
  label: string;
  tone: 'success' | 'danger' | 'muted';
  icon: 'check' | 'x' | null;
  hint: string;
}

const DEMO: Activation = { label: 'Demo', tone: 'danger', icon: 'x', hint: 'Runs with the VA watermark until you buy it' };

/**
 * In the app: whether the product runs unlocked on this computer, decided by the same license
 * file and rules the plugins use. A browser has no "this computer", so owned products say Owned.
 */
function activationOf(item: LibraryItem, license: LicenseStatus | null): Activation | null {
  const owned = !!item.product.ownership?.owned;
  if (!isDesktop) {
    if (owned) return { label: 'Owned', tone: 'success', icon: 'check', hint: 'Unlocks on every computer where you sign in to the VA Plugin Store app' };
    return item.demo ? DEMO : null;
  }
  if (!license) return null;
  if (license.codes.some((c) => c.code === item.product.code))
    return { label: 'Activated', tone: 'success', icon: 'check', hint: 'Unlocked on this computer: no watermark' };
  if (owned && license.syncing) return { label: 'Activating…', tone: 'muted', icon: null, hint: 'Getting this computer’s license from the store' };
  if (!owned) return DEMO;
  return { ...DEMO, hint: `Runs with the VA watermark on this computer${license.error ? `: ${license.error.message}` : '. Refresh licenses in Settings.'}` };
}

function ActivationPill({ activation: a }: { activation: Activation }) {
  return (
    <span className={`pill ${a.tone}`} title={a.hint}>
      {a.icon === 'check' && <IconCheck size={13} />}
      {a.icon === 'x' && <IconClose size={13} />}
      {a.label}
    </span>
  );
}

function Row({ item, license }: { item: LibraryItem; license: LicenseStatus | null }) {
  const installs = useInstalls();
  const toast = useToast();
  const qc = useQueryClient();
  const cart = useCart();
  const nav = useNavigate();
  const p = item.product;
  const inCart = cart.has(p.id);
  const own = ownershipOf(item);
  const activation = activationOf(item, license);
  const build = pickBuild(item.builds);
  const installed = installs.get(p.id);
  const progress = installs.progress[p.id];
  const active = !!p.ownership?.owned;
  // Demos install from here too; they run with the watermark until bought.
  const canInstall = active || !!item.demo;

  const act = async (fn: () => Promise<void>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const webDownload = async (id: string) => {
    try {
      const t = await api.post<DownloadTicket>(`/downloads/${id}`);
      window.location.href = t.url;
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const removeDemo = () =>
    act(async () => {
      await api.del(`/library/demos/${p.id}`);
      await qc.invalidateQueries({ queryKey: ['library'] });
    }, `${p.name} removed from My Library.`);

  return (
    <div className="list-row">
      <Link to={`/products/${p.slug}`} className="list-thumb">
        {p.thumbnailUrl && <img src={mediaUrl(p.thumbnailUrl)} alt="" />}
      </Link>
      <div className="grow">
        <Link to={`/products/${p.slug}`} style={{ color: 'var(--va-headline)', fontWeight: 600, fontSize: 16 }}>{p.name}</Link>
        <div className="row wrap small" style={{ gap: 8, marginTop: 4 }}>
          {activation && <ActivationPill activation={activation} />}
          {installed && <span className="pill">Installed v{installed.version}</span>}
          {own &&
            (own.tone === 'plain' ? (
              <span className="dim" title={own.hint}>{own.label}</span>
            ) : (
              <span className={`pill ${own.tone}`} title={own.hint}>{own.label}</span>
            ))}
          {build && <span className="dim mono">Latest v{build.version}</span>}
        </div>
      </div>
      <div className="row" style={{ gap: 6 }}>
        {isDesktop &&
          (progress ? (
            <span className="row small mono" style={{ gap: 8 }}>
              <span className="spinner" /> {progress.total ? Math.round((progress.received / progress.total) * 100) : 0}%
            </span>
          ) : !build ? (
            <span className="small dim">No build for this computer</span>
          ) : installed ? (
            <>
              {needsUpdate(installed, build) && (
                <button
                  className="btn btn-sm btn-indigo"
                  onClick={() => act(() => installs.install({ id: p.id, name: p.name }, build), installedMessage(p.name, build.installType, { demo: !active, updatedTo: build.version }))}
                >
                  <IconRefresh size={14} /> Update
                </button>
              )}
              <button className="icon-btn" title={installed.appDir ? 'Show the plugin folder' : 'Show in folder'} onClick={() => installs.reveal(p.id)}><IconFolder size={16} /></button>
              {installed.appDir && (
                <button className="btn btn-sm btn-outline" title="Opens the app's folder in Downloads" onClick={() => installs.reveal(p.id, 'app')}>
                  <IconMonitor size={14} /> Show app
                </button>
              )}
              <button className="icon-btn" title="Uninstall" onClick={() => act(() => installs.uninstall(p.id), `${p.name} removed.`)}><IconTrash size={16} /></button>
            </>
          ) : (
            canInstall && (
              <button
                className={active ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-outline'}
                onClick={() => act(() => installs.install({ id: p.id, name: p.name }, build), installedMessage(p.name, build.installType, { demo: !active }))}
              >
                <IconDownload size={14} /> Install
              </button>
            )
          ))}
        {!isDesktop && canInstall &&
          item.builds.map((b) => (
            <button key={b.id} className="btn btn-sm btn-outline" onClick={() => webDownload(b.id)} title={`${b.fileName} · ${formatBytes(b.sizeBytes)}`}>
              <IconDownload size={14} /> {b.platform.startsWith('mac') ? 'macOS' : b.platform.startsWith('win') ? 'Windows' : 'Download'}
            </button>
          ))}
        {!active && (
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                cart.add(p.id);
                nav('/cart');
              }}
            >
              Buy now
            </button>
            <button className="btn btn-sm btn-outline" onClick={() => (inCart ? nav('/cart') : (cart.add(p.id), toast.success(`${p.name} added to your cart.`)))}>
              <IconCart size={14} /> {inCart ? 'In cart' : 'Add to cart'}
            </button>
          </>
        )}
        {!active && item.demo && !installed && (
          <button className="icon-btn" title="Remove from My Library" onClick={removeDemo}><IconClose size={16} /></button>
        )}
      </div>
    </div>
  );
}

export function LibraryPage() {
  const { data, error, isLoading, refetch } = useLibrary();
  const installs = useInstalls();
  const license = useLicenseStatus();
  const qc = useQueryClient();
  const items = data ?? [];
  // Demos installed on this computer that aren't in the Library (installed before "Try" existed,
  // or with another account).
  const otherDemoInstalls = installs.items.filter((i) => !items.some((o) => o.product.id === i.productId && (o.product.ownership?.owned || o.demo)));

  // A demo installed on this computer belongs in the Library (with its Buy buttons), however it
  // got installed. Asked once per product; one the store no longer offers stays listed at the end.
  const adopted = useRef(new Set<string>());
  const pendingKey = otherDemoInstalls.map((d) => d.productId).join(',');
  useEffect(() => {
    if (!isDesktop || !data) return;
    const pending = otherDemoInstalls.filter((d) => !adopted.current.has(d.productId));
    if (pending.length === 0) return;
    pending.forEach((d) => adopted.current.add(d.productId));
    void Promise.allSettled(pending.map((d) => api.post('/library/demos', { productId: d.productId }))).then((results) => {
      if (results.some((r) => r.status === 'fulfilled')) void qc.invalidateQueries({ queryKey: ['library'] });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pendingKey]);

  // The store and this computer's license disagree: bought elsewhere, refunded or revoked since the
  // app last refreshed (every few hours). Refresh now, so the Library and the plugins catch up.
  const mismatchKey =
    isDesktop && license && data
      ? items.filter((i) => !!i.product.ownership?.owned !== license.codes.some((c) => c.code === i.product.code)).map((i) => i.product.id).join(',')
      : '';
  useEffect(() => {
    if (mismatchKey && desktop) void desktop.license.sync().catch(() => {});
  }, [mismatchKey]);

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <span className="eyebrow">Your products</span>
          <h1>My Library</h1>
        </div>
        <Link to="/redeem" className="btn btn-outline btn-sm">Redeem a code</Link>
      </div>
      {!isDesktop && (
        <div className="notice small" style={{ marginBottom: 18 }}>
          Install the <Link to="/download">VA Plugin Store app</Link> to install plugins in one click and keep their licenses up to date. Downloads here are for manual installs.
        </div>
      )}
      {error && <ErrorBox error={error} onRetry={() => refetch()} />}
      {isLoading && <Loading />}
      {data && items.length === 0 && otherDemoInstalls.length === 0 && (
        <Empty title="Nothing here yet" action={<Link to="/" className="btn btn-primary">Browse the store</Link>}>
          Products you buy, redeem, trial or try show up here on every computer you sign in to.
        </Empty>
      )}
      {/* One list: each row's badge says whether it's activated, a demo or expired. */}
      {(items.length > 0 || (isDesktop && otherDemoInstalls.length > 0)) && (
        <div className="card" style={{ padding: 0 }}>
          {items.map((i) => (
            <Row key={i.product.id} item={i} license={license} />
          ))}
          {isDesktop &&
            otherDemoInstalls.map((d) => (
              <div className="list-row" key={d.productId}>
                <div className="grow">
                  <div style={{ color: 'var(--va-headline)', fontWeight: 600 }}>{d.productName}</div>
                  <div className="row wrap small" style={{ gap: 8, marginTop: 4 }}>
                    <ActivationPill activation={DEMO} />
                    <span className="dim mono">v{d.version} · {d.targetDir}</span>
                  </div>
                </div>
                <button className="icon-btn" title="Uninstall" onClick={() => installs.uninstall(d.productId)}><IconTrash size={16} /></button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
